import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Container, Box } from '@mui/material';

import AddonsHeader from '../components/addons/page/AddonsHeader';
import AddonsSearchBar from '../components/addons/page/AddonsSearchBar';
import PaginationBar from '../components/addons/page/PaginationBar';

import AddonsTable from '../components/addons/table/AddonsTable';
import AddonDialog from '../components/addons/dialog/AddonDialog';

import { listAddons, createAddon, updateAddon, deleteAddon } from '../api/addons';
import { listNotificationHistory, sendAddonNotification } from '../api/notifications';

const LIMIT = 20;

export default function AddonsPage() {
  const [q, setQ] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [page, setPage] = useState(1);

  const [data, setData] = useState({ items: [], page: 1, totalPages: 1 });
  const rows = data?.items || [];

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [sendingId, setSendingId] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit'
  const [editing, setEditing] = useState(null);

  const openCreate = () => {
    setDialogMode('create');
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setDialogMode('edit');
    setEditing(row);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [resp, historyResp] = await Promise.all([
        listAddons({ q: searchQ, page, limit: LIMIT }),
        listNotificationHistory(),
      ]);
      setNotificationHistory(Array.isArray(historyResp?.items) ? historyResp.items : []);

      if (Array.isArray(resp)) {
        setData({ items: resp, page: 1, totalPages: 1 });
      } else {
        setData({
          items: resp.items || resp.data || [],
          page: resp.page || page,
          totalPages: resp.totalPages || 1,
        });
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [searchQ, page]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSubmit = useCallback(
    async (payload) => {
      if (dialogMode === 'create') {
        await createAddon(payload);
      } else if (editing?._id) {
        await updateAddon(editing._id, payload);
      }
      await fetchList();
    },
    [dialogMode, editing?._id, fetchList]
  );

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this addon? Images and addon files hosted on OVH will also be permanently deleted.')) return;
    await deleteAddon(id);
    await fetchList();
  };

  const sentByAddon = useMemo(() => {
    const result = new Map();
    notificationHistory.forEach((item) => {
      if (!item?.success || item?.topic !== 'Global') return;
      const id = String(item.addonId || '');
      if (id && !result.has(id)) result.set(id, item);
    });
    return result;
  }, [notificationHistory]);

  const handleSendNotification = async (addon) => {
    const previous = sentByAddon.get(String(addon._id));
    const prompt = previous
      ? `This addon was already sent to topic Global on ${new Date(previous.createdAt).toLocaleString()}. Send it again?`
      : `Send notification for “${addon.title}” to topic Global?`;
    if (!window.confirm(prompt)) return;

    setSendingId(addon._id);
    setErr('');
    setNotice('');
    try {
      await sendAddonNotification(addon._id, Boolean(previous));
      await fetchList();
      setNotice(`Notification for “${addon.title}” was sent to topic Global.`);
    } catch (error) {
      if (error?.response?.status === 409 && window.confirm('This addon has already been sent to Global. Send it again?')) {
        try {
          await sendAddonNotification(addon._id, true);
          await fetchList();
          setNotice(`Notification for “${addon.title}” was sent again to topic Global.`);
          return;
        } catch (retryError) {
          setErr(retryError?.response?.data?.message || retryError?.message || 'Send failed');
          return;
        }
      }
      setErr(error?.response?.data?.message || error?.message || 'Send failed');
    } finally {
      setSendingId('');
    }
  };

  const totalPages = useMemo(() => {
    const t = Number(data?.totalPages || 1);
    return Number.isFinite(t) && t > 0 ? t : 1;
  }, [data?.totalPages]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <AddonsHeader
        onNew={openCreate}
        onLogout={() => {
          localStorage.removeItem('isLoggedIn');
          window.location.reload();
        }}
      />

      <AddonsSearchBar
        q={q}
        onChangeQ={setQ}
        onEnterSearch={() => {
          setSearchQ(q.trim());
          setPage(1);
        }}
        onClickSearch={() => {
          setSearchQ(q.trim());
          setPage(1);
        }}
      />

      {err && <Box sx={{ mb: 2, color: 'error.main' }}>{err}</Box>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <AddonsTable
        rows={rows}
        loading={loading}
        onEdit={openEdit}
        onDelete={handleDelete}
        sentByAddon={sentByAddon}
        sendingId={sendingId}
        onSendNotification={handleSendNotification}
      />

      <AddonDialog
        open={dialogOpen}
        mode={dialogMode}
        editing={editing}
        onClose={closeDialog}
        onSubmit={handleSubmit}
      />

      <PaginationBar page={page} totalPages={totalPages} onChange={setPage} />
    </Container>
  );
}
